import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { Grid } from '../../engine/data/common/Grid';
import { GenerationModelDetails } from '../dicom/dicomBase';
import { ImageSlice } from '../dicom/imageSlice';
import { Modality } from '../dicom/modality';
import { DicomSeries, Series } from '../dicom/series';
import { WindowLevel } from './windowLevel';

function computeWindowing(data: Int16Array): WindowLevel {
    const histogram = new Map<number, number>();
    for (const d of data) {
        histogram.set(d, (histogram.get(d) ?? 0) + 1);
    }

    const imagePixels = data.length;
    // cutoff a portion of the histogram to avoid individual pixels at the edges of the histogram affecting the window level
    const minCutoff = Math.floor(imagePixels * 0.05);
    const maxCutoff = Math.floor(imagePixels * 0.995);
    let minCount = 0;
    let maxCount = 0;
    let minIdx = 0,
        maxIdx = 0;

    const sortedHistogram = Array.from(histogram.entries()).sort(
        (a, b) => a[0] - b[0]
    );

    for (const [key, value] of sortedHistogram) {
        if (minCount < minCutoff) {
            minCount += value;
            minIdx = key;
        }
        if (maxCount < maxCutoff) {
            maxCount += value;
            maxIdx = key;
        }
    }

    return new WindowLevel(maxIdx - minIdx, (minIdx + maxIdx) * 0.5);
}

/**
 * {@link Image3D} is a container for 3D image information parsed from DICOM {@link ImageSlice} objects.
 */
export class Image3D {
    public static readonly SliceResolutionTolerance = 0.2; // mm

    /**
     * Create a {@link Image3D} from DICOM object, including the image slice data
     * @param series {@link Series}
     * @returns {@link Image3D}
     */
    static fromSeries(series: Series): Image3D | null {
        const imageSlices = series.dicomImageSlices;

        if (imageSlices.length === 0 || !series.is3DImage) {
            return null;
        }

        const exampleSlice = imageSlices[0];

        const resolution = vec3.fromValues(
            exampleSlice.pixelSpacing[0],
            exampleSlice.pixelSpacing[1],
            Number.NaN
        );
        const size = vec3.fromValues(
            exampleSlice.cols,
            exampleSlice.rows,
            imageSlices.length
        );

        const zdir = vec3.fromValues(
            exampleSlice.imageOrientation[6],
            exampleSlice.imageOrientation[7],
            exampleSlice.imageOrientation[8]
        );

        // Slice positions (z-coordinates) projected onto the z-axis
        // (zdir is the normal of the slice plane, so we can use it to project the image position onto the z-axis)
        const slicePositions = new Array<[number, string]>();

        let bottomSlicePos: ReadonlyVec3 = vec3.create();
        let minz = Number.MAX_SAFE_INTEGER;

        for (const imageSlice of imageSlices) {
            const imagePos = imageSlice.imagePosition;
            const zcoord = vec3.dot(zdir, imagePos);

            slicePositions.push([zcoord, imageSlice.sopInstanceUID]);
            if (zcoord < minz) {
                bottomSlicePos = imagePos;
                minz = zcoord;
            }
        }

        slicePositions.sort((a, b) => a[0] - b[0]);

        // calculate the gaps between slices, along with slice UIDs
        const gaps: Array<[number, string]> = slicePositions
            .slice(1)
            .map((pos, i) => [
                Math.abs(pos[0] - slicePositions[i][0]),
                `${pos[1]} - ${slicePositions[i][1]}`,
            ]);

        gaps.sort((a, b) => a[0] - b[0]);

        // calculate the reolution from the size of the image and the number of slices (DICOM positions are in the middle of each slice, so adjust by 1 slice)
        const zResolution =
            (slicePositions[slicePositions.length - 1][0] -
                slicePositions[0][0]) /
            (size[2] - 1);

        // check for uniformity of the slice collection (no gaps, no extra slices)
        const invalidGaps = gaps.filter(
            (gap) =>
                Math.abs(gap[0] - zResolution) >
                Image3D.SliceResolutionTolerance
        );

        if (invalidGaps.length > 0) {
            throw new Error(
                `Non-uniform slice spacing detected, this is not supported. Offending slices: ${invalidGaps
                    .map((gap) => gap[1])
                    .join(', ')}`
            );
        }

        // Currently we only support constant slice spacing and assume that there are no missing slices.
        // To support slice interpolation, further methods to check if slices are missing, and to interpolate them
        // should be implemented.
        // if we have only a single slice, try to access the SliceThickness tag, if that fails, use 1.0mm
        if (slicePositions.length >= 2) {
            resolution[2] = zResolution;
        } else if (
            slicePositions.length === 1 &&
            exampleSlice.sliceThickness > 0
        ) {
            resolution[2] = exampleSlice.sliceThickness;
        } else {
            resolution[2] = 1.0;
        }

        return new Image3D(
            Grid.fromDicom(
                size,
                resolution,
                bottomSlicePos,
                exampleSlice.imageOrientation
            ),
            series.seriesInstanceUID,
            series.frameOfReferenceUid,
            exampleSlice,
            series
        );
    }

    /**
     * Update the pixel data from the DICOM slices in the series
     */
    private updatePixelDataFromSeries(): void {
        for (const slice of this.series.dicomImageSlices) {
            const voxelCoords = this.grid.toVoxelSpace(slice.imagePosition);
            const sliceSize = this.grid.sizeX * this.grid.sizeY;
            const zcoord = Math.round(voxelCoords[2]);
            if (zcoord < 0 || zcoord >= this.grid.sizeZ) {
                throw new Error(
                    `Slice position outside of image volume (${slice.sopInstanceUID}, pos ${slice.imagePosition} - ${zcoord}}`
                );
            }
            const pixelIdx = zcoord * sliceSize;
            this.pixelData.set(slice.pixelData, pixelIdx);
        }
    }

    public hasSlice(sopInstanceUid: string): boolean {
        for (const slice of this.series.dicomImageSlices) {
            if (slice.sopInstanceUID === sopInstanceUid) {
                return true;
            }
        }
        return false;
    }

    public getUidForSlice(imagePos: number): string | undefined {
        const slice = this.series.dicomImageSlices.find(
            (slice) => Math.abs(slice.imagePosition[2] - imagePos) < 0.1
        );
        return slice?.sopInstanceUID ?? undefined;
    }

    public getDefaultWindowLevel(): WindowLevel {
        if (this.firstSlice.modality === Modality.CT) {
            return new WindowLevel(400, 20);
        } else {
            return computeWindowing(this.pixelData);
        }
    }

    public constructor(
        grid: Grid,
        dicomUid: string,
        forUid: string,
        public readonly firstSlice: ImageSlice, // TODO: remove this, needed now to construct the ImageObject,
        private readonly series: Series
    ) {
        this.grid = grid;
        this.dicomUid = dicomUid;
        this.frameOfReferenceUid = forUid;
        this._pixelData = new Int16Array(0);
    }

    public get generationModel(): GenerationModelDetails | undefined {
        const seriesObject = this.series.seriesObjects.find(
            (x) => x.generationModel
        );
        return seriesObject?.generationModel;
    }

    public get isNew(): boolean {
        return this.series.seriesObjects.some((x) => x.isNew);
    }

    public get isExported(): boolean {
        return this.series.seriesObjects.some((x) => x.isExported);
    }

    public setExported(): void {
        this.series.setExported();
    }

    public get minValue(): number {
        let min: number;
        min = this.pixelData[0];
        for (const d of this.pixelData) {
            if (d < min) {
                min = d;
            }
        }
        return min;
    }

    public readonly grid: Grid;

    public readonly dicomUid: string;

    public readonly frameOfReferenceUid: string;

    private _windowLevel: WindowLevel | undefined;

    public get windowLevel(): WindowLevel {
        if (!this._windowLevel) {
            this._windowLevel = this.getDefaultWindowLevel();
        }
        return this._windowLevel;
    }

    public set windowLevel(value: WindowLevel) {
        this._windowLevel = value;
        this.firstSlice.windowLevel = value;
    }

    private _pixelData: Int16Array;

    public get pixelData(): Int16Array {
        if (this._pixelData.length === 0) {
            this._pixelData = new Int16Array(
                this.grid.sizeX * this.grid.sizeY * this.grid.sizeZ
            );
            this.updatePixelDataFromSeries();
        }
        return this._pixelData;
    }

    public get seriesUid(): string {
        return this.series.seriesInstanceUID;
    }

    public get studyUid(): string {
        return this.firstSlice.studyInstanceUID;
    }

    public get length(): number {
        return this.series.numberOfObjects;
    }

    public get seriesMetadata(): DicomSeries {
        return this.series.seriesModule;
    }

    public get sopClassUid(): string {
        return this.firstSlice.datasetWrapper.sopClassUID;
    }

    /// Returns an iterator to get the slices as DICOM blobs
    public *getSlicesAsDicomBlobs(): IterableIterator<Blob> {
        for (const slice of this.series.dicomImageSlices) {
            yield slice.getAsDicomBlob();
        }
    }
}
